import type { EntryDto, Paginated, SessionDetailDto, SummaryItemRow, SummaryUomRow } from '@bnn/shared';
import { useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { VoidDialog } from '../components/VoidDialog';
import { Alert, Badge, Button, Card, Empty, Input, Pagination, Select, Table, useDebounced } from '../components/ui';
import { REVIEW_TOP_QTY } from '../count/guards';
import { api, qs } from '../lib/api';
import { dt, qty } from '../lib/format';
import { useApi } from '../lib/useApi';

type Tab = 'item' | 'uom' | 'entries';

const FLAG: Record<string, string> = {
  FACTOR_DIFFERS: 'Factor ต่างจาก Master',
  ITEM_INACTIVE_AT_SYNC: 'SAP เปลี่ยนเป็น Inactive หลังนับ',
  NOT_INVENTORY_AT_SYNC: 'ไม่ใช่สินค้าคงคลังตอนส่ง',
  INVENTORY_UOM_DIFFERS: 'Inventory UoM ต่างจาก Master',
  MASTER_HAS_CONVERSION: 'มี Conversion แล้ว (คำนวณใหม่ได้)',
};

/** Review screen body (Requirements §7.3): totals per counted UoM and per item in the inventory UoM. */
export function SessionReview({ session, onChanged }: { session: SessionDetailDto; onChanged: () => void }) {
  const [tab, setTab] = useState<Tab>('item');
  const [search, setSearch] = useState('');
  const [inactive, setInactive] = useState(false);
  const q = useDebounced(search.trim().toLowerCase(), 200);
  const [tick, setTick] = useState(0);
  const refresh = () => {
    setTick((t) => t + 1);
    onChanged();
  };

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(
          [
            ['item', 'สรุปต่อสินค้า (Inventory UoM)'],
            ['uom', 'ตามหน่วยที่นับ'],
            ['entries', 'รายการนับทั้งหมด'],
          ] as const
        ).map(([k, l]) => (
          <Button key={k} size="sm" variant={tab === k ? 'primary' : 'secondary'} onClick={() => setTab(k)}>
            {l}
          </Button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} /> เฉพาะสินค้า Inactive
        </label>
        <Input type="search" placeholder="กรองรหัส / ชื่อสินค้า" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-64 py-1.5 text-sm" />
      </div>
      {tab === 'item' && <ByItem sessionId={session.id} q={q} inactive={inactive} tick={tick} />}
      {tab === 'uom' && <ByUom sessionId={session.id} q={q} inactive={inactive} tick={tick} />}
      {tab === 'entries' && <Entries session={session} q={q} inactive={inactive} onChanged={refresh} />}
    </Card>
  );
}

const match = (q: string, r: { item_code: string; item_name: string }) => !q || `${r.item_code} ${r.item_name}`.toLowerCase().includes(q);

/** Counted while Inactive in SAP (v2.2): allowed, but Cost must check it. */
const InactiveBadge = () => <Badge tone="warn">Inactive</Badge>;

/** One of the session's largest entries — a typo (extra zero) shows up here. Does not block closing. */
const TopQtyBadge = () => <Badge tone="warn">ยอดสูง — ตรวจก่อนปิดรอบ</Badge>;

function ByItem({ sessionId, q, inactive, tick }: { sessionId: string; q: string; inactive: boolean; tick: number }) {
  const { data, error } = useApi<SummaryItemRow[]>(`/sessions/${sessionId}/summary?by=item&t=${tick}`);
  const rows = useMemo(() => (data ?? []).filter((r) => match(q, r) && (!inactive || r.item_inactive)), [data, q, inactive]);
  const inactiveCount = (data ?? []).filter((r) => r.item_inactive).length;
  const pending = rows.filter((r) => r.pending_qty !== '0.0000');
  if (error) return <Alert>{error.message}</Alert>;
  if (data && !data.length) return <Empty>ยังไม่มีรายการนับ</Empty>;
  return (
    <>
      {pending.length > 0 && (
        <div className="mb-3">
          <Alert tone="warn">
            มี {pending.length} สินค้าที่มีรายการ "รอแปลงหน่วย" — ยอดส่วนนั้นไม่ถูกรวมใน Inventory UoM (ดูคอลัมน์ รอแปลงหน่วย)
          </Alert>
        </div>
      )}
      {inactiveCount > 0 && (
        <div className="mb-3">
          <Alert tone="warn">มี {inactiveCount} สินค้าที่นับขณะเป็น Inactive ใน SAP — ตรวจสอบกับทีม Cost (ติ๊ก "เฉพาะสินค้า Inactive" เพื่อดูเฉพาะรายการนี้)</Alert>
        </div>
      )}
      <Table head={['รหัส', 'ชื่อสินค้า', 'นับเป็น', 'รวม (Inventory UoM)', 'รอแปลงหน่วย', 'ครั้ง']} minWidth={720}>
        {rows.map((r) => (
          <tr key={r.item_code}>
            <td className="font-semibold whitespace-nowrap">{r.item_code}</td>
            <td>
              {r.item_name} {r.item_inactive && <InactiveBadge />}
            </td>
            <td className="text-xs">
              {r.uoms.map((u) => (
                <div key={u.uom_code}>
                  {qty(u.original_qty)} {u.uom_code} {u.pending && <Badge tone="warn">รอแปลง</Badge>}
                </div>
              ))}
            </td>
            <td className="text-right whitespace-nowrap">
              <b>{qty(r.converted_qty)}</b> {r.inventory_uom}
            </td>
            <td className="text-right">{r.pending_qty !== '0.0000' ? <span className="text-warn">{qty(r.pending_qty)}</span> : '-'}</td>
            <td className="text-right">{r.entry_count}</td>
          </tr>
        ))}
      </Table>
      <p className="mt-2 text-sm text-muted">{rows.length.toLocaleString()} สินค้า · ไม่รวมรายการที่ยกเลิก</p>
    </>
  );
}

function ByUom({ sessionId, q, inactive, tick }: { sessionId: string; q: string; inactive: boolean; tick: number }) {
  const { data, error } = useApi<SummaryUomRow[]>(`/sessions/${sessionId}/summary?by=uom&t=${tick}`);
  const rows = useMemo(() => (data ?? []).filter((r) => match(q, r) && (!inactive || r.item_inactive)), [data, q, inactive]);
  if (error) return <Alert>{error.message}</Alert>;
  if (data && !data.length) return <Empty>ยังไม่มีรายการนับ</Empty>;
  return (
    <Table head={['รหัส', 'ชื่อสินค้า', 'หน่วยที่นับ', 'จำนวนที่นับ', 'แปลงเป็น', 'ครั้ง']} minWidth={720}>
      {rows.map((r) => (
        <tr key={`${r.item_code}|${r.uom_code}`}>
          <td className="font-semibold whitespace-nowrap">{r.item_code}</td>
          <td>
            {r.item_name} {r.item_inactive && <InactiveBadge />}
          </td>
          <td>{r.uom_code}</td>
          <td className="text-right">{qty(r.original_qty)}</td>
          <td className="text-right whitespace-nowrap">
            {r.pending_qty !== '0.0000' ? (
              <Badge tone="warn">รอแปลงหน่วย</Badge>
            ) : (
              <>
                {qty(r.converted_qty)} {r.inventory_uom}
              </>
            )}
          </td>
          <td className="text-right">{r.entry_count}</td>
        </tr>
      ))}
    </Table>
  );
}

function Entries({ session, q, inactive, onChanged }: { session: SessionDetailDto; q: string; inactive: boolean; onChanged: () => void }) {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [showVoid, setShowVoid] = useState(true);
  const [sort, setSort] = useState<'counted_desc' | 'qty_desc'>('counted_desc');
  const list = useApi<Paginated<EntryDto>>(
    `/sessions/${session.id}/entries${qs({ page, page_size: 100, sort, include_void: showVoid ? 'true' : 'false', item_code: /^[A-Za-z0-9-]+$/.test(q) && q.length > 3 ? q.toUpperCase() : undefined })}`,
  );
  // The session's largest entries (whatever page or order is shown): check them before closing.
  const top = useApi<Paginated<EntryDto>>(`/sessions/${session.id}/entries${qs({ page_size: REVIEW_TOP_QTY, sort: 'qty_desc', include_void: 'false' })}`);
  const topIds = useMemo(() => new Set((top.data?.data ?? []).filter((e) => Number(e.original_qty) > 0).map((e) => e.id)), [top.data]);
  const [target, setTarget] = useState<EntryDto | null>(null);
  const canVoid = can('entries.voidAny') && (session.status === 'OPEN' || session.status === 'REVIEW');
  const rows = (list.data?.data ?? []).filter((r) => match(q, r) && (!inactive || r.item_inactive));

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={showVoid} onChange={(e) => (setShowVoid(e.target.checked), setPage(1))} /> แสดงรายการที่ยกเลิก
        </label>
        <label className="flex items-center gap-2">
          เรียงตาม
          <Select value={sort} onChange={(e) => (setSort(e.target.value as typeof sort), setPage(1))} className="w-auto py-1 text-sm">
            <option value="counted_desc">เวลานับ (ล่าสุดก่อน)</option>
            <option value="qty_desc">จำนวน (มาก → น้อย)</option>
          </Select>
        </label>
      </div>
      {topIds.size > 0 && (
        <p className="mb-2 text-xs text-muted">
          {topIds.size} รายการที่จำนวนสูงสุดของรอบมีป้าย <TopQtyBadge /> — เรียงตามจำนวนเพื่อตรวจ (ไม่บังคับ ปิดรอบได้ตามปกติ)
        </p>
      )}
      {list.error && <Alert>{list.error.message}</Alert>}
      {list.data && list.data.total === 0 ? (
        <Empty>ยังไม่มีรายการนับ</Empty>
      ) : (
        <>
          <Table head={['เวลานับ', 'สินค้า', 'จำนวน', 'แปลงเป็น', 'ผู้นับ / วิธี', '']} minWidth={820}>
            {rows.map((e) => (
              <tr key={e.id} className={e.is_void ? 'text-muted' : ''}>
                <td className="text-xs whitespace-nowrap">
                  {dt(e.counted_at)}
                  {Math.abs(new Date(e.received_at).getTime() - new Date(e.counted_at).getTime()) > 5 * 60_000 && (
                    <div title="เวลาที่ Server ได้รับ">รับ {dt(e.received_at)}</div>
                  )}
                </td>
                <td className={e.is_void ? 'line-through' : ''}>
                  <b>{e.item_code}</b> {e.item_name} {e.item_inactive && <InactiveBadge />}
                  {e.flags.map((f) => (
                    <div key={f}>
                      <Badge tone="warn">{FLAG[f] ?? f}</Badge>
                    </div>
                  ))}
                </td>
                <td className={`text-right whitespace-nowrap ${e.is_void ? 'line-through' : ''}`}>
                  {qty(e.original_qty)} {e.uom_code}
                  {!e.is_void && topIds.has(e.id) && (
                    <div>
                      <TopQtyBadge />
                    </div>
                  )}
                </td>
                <td className="text-right whitespace-nowrap">
                  {e.conversion_status === 'OK' ? (
                    <>
                      {qty(e.converted_qty)} {e.inventory_uom}
                      {e.uom_code !== e.inventory_uom && <div className="text-xs text-muted">×{e.conversion_factor}</div>}
                    </>
                  ) : (
                    <Badge tone="warn">รอแปลงหน่วย</Badge>
                  )}
                </td>
                <td className="text-xs">
                  {e.user.display_name}
                  <div className="text-muted">
                    {e.entry_method === 'SCAN' ? `สแกน ${e.barcode}` : 'ค้นหา'}
                    {e.unbound_barcode && ` · ไม่ผูก BC ${e.unbound_barcode}`}
                    {e.replaces_entry_id && ' · แก้ยอด'}
                  </div>
                </td>
                <td className="text-right whitespace-nowrap">
                  {e.is_void ? (
                    <span className="text-xs">
                      <Badge>ยกเลิก</Badge>
                      <div>{e.void_reason}</div>
                    </span>
                  ) : (
                    canVoid && (
                      <Button size="sm" variant="danger" onClick={() => setTarget(e)}>
                        ยกเลิก
                      </Button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </Table>
          {list.data && <Pagination page={list.data.page} pageSize={list.data.page_size} total={list.data.total} onPage={setPage} />}
        </>
      )}
      <VoidDialog
        open={!!target}
        title="ยกเลิกรายการนี้ (ยอดจะถูกหักออก)"
        placeholder="เช่น คีย์ผิด, นับซ้ำ — ไม่ต้องใส่ยอดใหม่ที่นี่"
        summary={target ? `${target.item_code} ${target.item_name} · ${qty(target.original_qty)} ${target.uom_code} · ${target.user.display_name} ${dt(target.counted_at)}` : undefined}
        onClose={() => setTarget(null)}
        onConfirm={async (reason) => {
          await api.post(`/entries/${target!.id}/void`, { op_id: crypto.randomUUID(), reason });
          setTarget(null);
          list.reload();
          top.reload();
          onChanged();
        }}
      />
    </>
  );
}
